import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ProductCard from "@/components/ProductCard";
import { OctopusMascot } from "@/components/Mascot";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = [
  { value: "newest", label: "Mais recentes" },
  { value: "best_selling", label: "Mais vendidos" },
  { value: "price_asc", label: "Menor preço" },
  { value: "price_desc", label: "Maior preço" },
  { value: "name", label: "Nome (A-Z)" },
];

export default function ProductsPage() {
  const { categories, addToCart } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const q = searchParams.get("q") || "";
  const category = searchParams.get("category") || "";
  const color = searchParams.get("color") || "";
  const available = searchParams.get("available") || "";
  const minPrice = searchParams.get("min_price") || "";
  const maxPrice = searchParams.get("max_price") || "";
  const sort = searchParams.get("sort") || "newest";

  const [searchInput, setSearchInput] = useState(q);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category) params.category = category;
    if (color) params.color = color;
    if (available) params.available = available;
    if (minPrice) params.min_price = minPrice;
    if (maxPrice) params.max_price = maxPrice;
    params.sort = sort;
    api.get("/products", { params })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [q, category, color, available, minPrice, maxPrice, sort]);

  const allColors = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.colors || []).forEach((c) => set.add(c)));
    return [...set];
  }, [products]);

  const activeFilters = [category, color, available, minPrice, maxPrice].filter(Boolean).length;

  const filters = (
    <div className="flex flex-col gap-5" data-testid="filters-panel">
      <div>
        <p className="text-sm font-bold text-slate-700 mb-2">Categoria</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setParam("category", "")}
            className={`text-xs font-bold px-3.5 py-2 rounded-full transition-colors ${!category ? "bg-brand-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-pink-50"}`}
            data-testid="filter-category-all"
          >
            Todas
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => setParam("category", c.id === category ? "" : c.id)}
              className={`text-xs font-bold px-3.5 py-2 rounded-full transition-colors ${category === c.id ? "bg-brand-gradient text-white" : "bg-slate-100 text-slate-600 hover:bg-pink-50"}`}
              data-testid={`filter-category-${c.id}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-sm font-bold text-slate-700 mb-2">Faixa de preço</p>
        <div className="flex items-center gap-2">
          <input
            type="number" min="0" placeholder="Mín" value={minPrice}
            onChange={(e) => setParam("min_price", e.target.value)}
            data-testid="filter-min-price"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300"
          />
          <span className="text-slate-400">—</span>
          <input
            type="number" min="0" placeholder="Máx" value={maxPrice}
            onChange={(e) => setParam("max_price", e.target.value)}
            data-testid="filter-max-price"
            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300"
          />
        </div>
      </div>

      {allColors.length > 0 && (
        <div>
          <p className="text-sm font-bold text-slate-700 mb-2">Cor</p>
          <Select value={color || "all"} onValueChange={(v) => setParam("color", v === "all" ? "" : v)}>
            <SelectTrigger className="rounded-xl" data-testid="filter-color"><SelectValue placeholder="Todas as cores" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas as cores</SelectItem>
              {allColors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <p className="text-sm font-bold text-slate-700 mb-2">Disponibilidade</p>
        <Select value={available || "all"} onValueChange={(v) => setParam("available", v === "all" ? "" : v)}>
          <SelectTrigger className="rounded-xl" data-testid="filter-availability"><SelectValue placeholder="Todos" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="true">Disponíveis</SelectItem>
            <SelectItem value="false">Esgotados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {activeFilters > 0 && (
        <button
          onClick={() => setSearchParams(q ? { q } : {})}
          className="text-xs font-bold text-pink-600 flex items-center gap-1 hover:underline"
          data-testid="clear-filters-btn"
        >
          <X size={13} /> Limpar filtros ({activeFilters})
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900" data-testid="products-title">
            {q ? `Resultados para "${q}"` : "Produtos"}
          </h1>
          <form
            onSubmit={(e) => { e.preventDefault(); setParam("q", searchInput.trim()); }}
            className="mt-5 max-w-xl"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="O que você está procurando? 🔍"
                data-testid="products-search-input"
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 bg-slate-50 focus:bg-white focus:border-pink-300 focus:ring-2 focus:ring-pink-100 outline-none text-sm transition-all"
              />
            </div>
          </form>
        </div>

        <div className="flex items-center justify-between gap-3 mb-6">
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className="lg:hidden inline-flex items-center gap-2 text-sm font-bold text-slate-700 bg-slate-100 px-4 py-2.5 rounded-full"
            data-testid="filters-toggle-btn"
          >
            <SlidersHorizontal size={16} /> Filtros {activeFilters > 0 && `(${activeFilters})`}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-400 hidden sm:inline">Ordenar:</span>
            <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-44 rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          <aside className={`${filtersOpen ? "block" : "hidden"} lg:block bg-slate-50 lg:bg-transparent rounded-2xl p-5 lg:p-0`}>
            {filters}
          </aside>

          <div>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="rounded-2xl bg-slate-100 aspect-[3/4] animate-pulse" />
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="flex flex-col items-center text-center py-20 gap-4" data-testid="search-empty-state">
                <OctopusMascot size={110} className="animate-float-slower" />
                <div>
                  <p className="font-bold text-slate-800 text-lg">Nenhum produto encontrado</p>
                  <p className="text-sm text-slate-500 mt-1">Tente outra busca ou limpe os filtros.</p>
                </div>
                <button
                  onClick={() => { setSearchInput(""); setSearchParams({}); }}
                  className="bg-brand-gradient text-white font-bold px-6 py-3 rounded-full text-sm shadow-soft active:scale-95 transition-transform"
                  data-testid="empty-clear-btn"
                >
                  Limpar busca
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-400 mb-4" data-testid="products-count">{products.length} {products.length === 1 ? "produto" : "produtos"}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                  {products.map((p) => (
                    <ProductCard key={p.id} product={p} onAdd={(prod) => addToCart(prod, 1)} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <CartDrawer />
      <WhatsAppFloat />
    </div>
  );
}
